import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { append, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { tap } from 'rxjs/operators';
import { AdminNonConformanceRoutes } from 'src/app/core/enums/routes.enum';
import { ApiResponse } from 'src/app/core/models/api-response';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { IdName } from 'src/app/shared/models/id-name';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { IListSeverityModel } from '../../models/severities/list-severity.model';
import { SeverityApiService } from '../../services/severities/severity-api.service';
import {
  AddSeverity,
  DeleteSeverity,
  LoadAllSeverities,
  LoadSeverities,
  UpdateSeverity,
  LoadSingleSeverity,
  ClearCurrentSeverity,
} from './severity.actions';
import { ISeverityModel } from '../../models/severities/severity.model';

export interface SeverityStateModel {
  loaded: boolean;
  severities: IListSeverityModel[] | undefined;
  allSeverities: IdName[];
  severitiesTotalPages: number;
  selectedSeverity: ISeverityModel | null;
}

const SEVERITY_STATE_TOKEN = new StateToken<SeverityStateModel>('severity');

@State<SeverityStateModel>({
  name: SEVERITY_STATE_TOKEN,
  defaults: {
    loaded: false,
    severities: undefined,
    allSeverities: [],
    severitiesTotalPages: 0,
    selectedSeverity: null,
  },
})
@Injectable()
export class SeverityState {
  constructor(private service: SeverityApiService) {}

  @Action(LoadSeverities, { cancelUncompleted: true })
  loadSeverities(ctx: StateContext<SeverityStateModel>, { filter }: LoadSeverities) {
    return this.service.getSeverities(filter).pipe(
      tap((response: ApiResponse<IListSeverityModel[]>) => {
        ctx.setState(
          patch({ severities: response.data, severitiesTotalPages: response.totalPages })
        );
      })
    );
  }

  @Action(LoadAllSeverities, { cancelUncompleted: true })
  loadAllSeverities(ctx: StateContext<SeverityStateModel>) {
    return this.service.getAllSeverities().pipe(
      tap((allSeverities: IdName[]) => {
        ctx.setState(patch({ allSeverities }));
      })
    );
  }

  @Action(AddSeverity, { cancelUncompleted: true })
  @OnSuccess({ url: AdminNonConformanceRoutes.SeverityTypes, message: 'severityCreated' })
  addSeverity(ctx: StateContext<SeverityStateModel>, { severity }: AddSeverity) {
    return this.service.createSeverity(severity).pipe(
      tap((response: IListSeverityModel) => {
        ctx.setState(
          patch({
            severities: append([response]),
          })
        );
      })
    );
  }

  @Action(UpdateSeverity, { cancelUncompleted: true })
  @OnSuccess({ url: AdminNonConformanceRoutes.SeverityTypes, message: 'severityUpdated' })
  updateSeverity(ctx: StateContext<SeverityStateModel>, { severity }: UpdateSeverity) {
    return this.service.updatedSeverity(severity).pipe(
      tap((response: IListSeverityModel) => {
        ctx.setState(
          patch({
            severities: updateItem<IListSeverityModel>(
              (severityToUpdate: IListSeverityModel | undefined) =>
                severityToUpdate?.id === response.id,
              response
            ),
          })
        );
      })
    );
  }

  @Action(DeleteSeverity, { cancelUncompleted: true })
  @OnSuccess({
    url: AdminNonConformanceRoutes.SeverityTypes,
    message: 'severityDeleted',
    dispatch: ResetCurrentFilter,
  })
  deleteSeverity(ctx: StateContext<SeverityStateModel>, { id }: DeleteSeverity) {
    return this.service.deleteSeverity(id).pipe(
      tap((response: IListSeverityModel) => {
        ctx.setState(
          patch({
            severities: removeItem<IListSeverityModel>(
              (severity: IListSeverityModel | undefined) => severity?.id === response.id
            ),
          })
        );
      })
    );
  }

  @Action(LoadSingleSeverity, { cancelUncompleted: true })
  loadSingleSeverity(ctx: StateContext<SeverityStateModel>, { id }: LoadSingleSeverity) {
    return this.service.getSeverityById(id).pipe(
      tap((response: ISeverityModel) => {
        ctx.setState(
          patch({
            selectedSeverity: response,
          })
        );
      })
    );
  }

  @Action(ClearCurrentSeverity)
  clearCurrentSeverity(ctx: StateContext<SeverityStateModel>) {
    ctx.setState(patch({ selectedSeverity: null }));
  }

  @Selector()
  static severitiesLoaded(state: SeverityStateModel) {
    return state.loaded;
  }

  @Selector()
  static getSeverities(state: SeverityStateModel) {
    return state.severities;
  }

  @Selector()
  static getAllSeverities(state: SeverityStateModel) {
    return state.allSeverities;
  }

  @Selector()
  static getTotalPagesForSeverities(state: SeverityStateModel) {
    return state.severitiesTotalPages;
  }

  @Selector()
  static getSeverityById(state: any) {
    return (id: string) => {
      return state.severities?.find((severity: IListSeverityModel) => severity.id === id);
    };
  }

  @Selector()
  static getSelectedSeverity(state: SeverityStateModel) {
    return state.selectedSeverity;
  }
}
